import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus, Trash2, Info } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

const FAMILY_LABELS = {
  sales: 'Sales & Growth',
  front_of_house: 'Front-of-House / Service',
  technical_craft: 'Technical / Craft',
  management: 'Management',
};

const LEVEL_NAMES = { 1: 'Needs Improvement', 2: 'Developing', 3: 'Meets Expectations', 4: 'Strong', 5: 'Excellent' };

const slugify = (text) => text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

function Rubrics({ rubrics, onSaved }) {
  const [activeFamily, setActiveFamily] = useState('sales');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const existing = rubrics.find((r) => r.roleFamily === activeFamily);
    setDraft(existing
      ? JSON.parse(JSON.stringify(existing))
      : { roleFamily: activeFamily, competencies: [], knockoutCriteria: [] });
  }, [activeFamily, rubrics]);

  if (!draft) return null;

  const updateLabel = (idx, label) => {
    setDraft((d) => {
      const competencies = [...d.competencies];
      const key = competencies[idx].key || slugify(label);
      competencies[idx] = { ...competencies[idx], label, key };
      return { ...d, competencies };
    });
  };

  const updateWeight = (idx, weight) => {
    setDraft((d) => {
      const competencies = [...d.competencies];
      competencies[idx] = { ...competencies[idx], weight };
      return { ...d, competencies };
    });
  };

  const updateAnchor = (idx, level, value) => {
    setDraft((d) => {
      const competencies = [...d.competencies];
      competencies[idx] = { ...competencies[idx], anchors: { ...competencies[idx].anchors, [level]: value } };
      return { ...d, competencies };
    });
  };

  const addCompetency = () => {
    setDraft((d) => ({
      ...d,
      competencies: [...d.competencies, {
        key: '', label: '', weight: 10,
        anchors: { 1: '', 2: '', 3: '', 4: '', 5: '' },
      }],
    }));
  };

  const removeCompetency = (idx) => {
    setDraft((d) => ({ ...d, competencies: d.competencies.filter((_, i) => i !== idx) }));
  };

  const addKnockout = () => {
    setDraft((d) => ({ ...d, knockoutCriteria: [...d.knockoutCriteria, { key: '', label: '', description: '' }] }));
  };

  const updateKnockoutLabel = (idx, label) => {
    setDraft((d) => {
      const knockoutCriteria = [...d.knockoutCriteria];
      const key = knockoutCriteria[idx].key || slugify(label);
      knockoutCriteria[idx] = { ...knockoutCriteria[idx], label, key };
      return { ...d, knockoutCriteria };
    });
  };

  const updateKnockoutDescription = (idx, description) => {
    setDraft((d) => {
      const knockoutCriteria = [...d.knockoutCriteria];
      knockoutCriteria[idx] = { ...knockoutCriteria[idx], description };
      return { ...d, knockoutCriteria };
    });
  };

  const removeKnockout = (idx) => {
    setDraft((d) => ({ ...d, knockoutCriteria: d.knockoutCriteria.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/recruitment/rubrics`, draft);
      toast.success('Rubric saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save rubric');
    } finally {
      setSaving(false);
    }
  };

  const totalWeight = draft.competencies.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);

  return (
    <div>
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6 flex gap-3">
        <Info size={18} className="text-purple-500 shrink-0 mt-0.5" />
        <p className="text-sm text-purple-900">
          A rubric is the checklist used to score every candidate fairly and consistently, whether
          it&rsquo;s a person reviewing or the AI. Each quality below (a &ldquo;competency&rdquo;) is
          scored 1 to 5, and the wording under each number is what a reviewer (or the AI) actually
          looks for at that level. <strong>Weight</strong> is how much that quality counts toward the
          overall score for this role family, higher weight matters more.
        </p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {Object.entries(FAMILY_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveFamily(key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              activeFamily === key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {draft.competencies.map((c, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500">Competency Name</label>
                <input
                  value={c.label} onChange={(e) => updateLabel(idx, e.target.value)}
                  placeholder="e.g. Composure Under Pressure"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium"
                />
              </div>
              <div className="w-28">
                <label className="text-xs font-medium text-gray-500">Weight</label>
                <input
                  type="number" value={c.weight} onChange={(e) => updateWeight(idx, Number(e.target.value))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <button onClick={() => removeCompetency(idx)} className="mt-6 text-red-400 hover:text-red-600" title="Remove">
                <Trash2 size={18} />
              </button>
            </div>

            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((level) => (
                <div key={level} className="flex items-start gap-3">
                  <span className="mt-2 w-32 shrink-0 text-xs font-medium text-gray-500">
                    {level} &middot; {LEVEL_NAMES[level]}
                  </span>
                  <textarea
                    value={c.anchors[level] || ''}
                    onChange={(e) => updateAnchor(idx, level, e.target.value)}
                    rows={1}
                    placeholder={`What a ${LEVEL_NAMES[level].toLowerCase()} candidate does...`}
                    className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm resize-y"
                  />
                </div>
              ))}
            </div>

            {c.key && <p className="mt-3 text-[11px] text-gray-300 font-mono">{c.key}</p>}
          </div>
        ))}
        <button onClick={addCompetency} className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800">
          <Plus size={16} /> Add Competency
        </button>
        {draft.competencies.length > 0 && (
          <p className="text-xs text-gray-400">
            Weights add up to {totalWeight}. They don&rsquo;t need to total any specific number, just
            reflect how much each quality matters relative to the others.
          </p>
        )}
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Automatic Rejections</h3>
        <p className="text-xs text-gray-500 mb-3">
          If any of these happen, the candidate is rejected regardless of how well they scored
          everywhere else.
        </p>
        <div className="space-y-2">
          {draft.knockoutCriteria.map((k, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <input
                value={k.label} onChange={(e) => updateKnockoutLabel(idx, e.target.value)}
                placeholder="e.g. Integrity Violation"
                className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium"
              />
              <input
                value={k.description} onChange={(e) => updateKnockoutDescription(idx, e.target.value)}
                placeholder="What counts as this, e.g. evidence of dishonesty or theft"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button onClick={() => removeKnockout(idx)} className="text-red-400 hover:text-red-600 mt-2"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
        <button onClick={addKnockout} className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 mt-2">
          <Plus size={16} /> Add Automatic Rejection Rule
        </button>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-8 bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Rubric'}
      </button>
    </div>
  );
}

export default Rubrics;
